<script setup lang="ts">
// Editing a collection (HRTH-164.6): its name and details, and its cover, either a photo of
// the library or an uploaded image (made smaller and encrypted on this device), or back to
// automatic (folder image, embedded art, first photo).
import { computed, reactive, ref } from 'vue'
import { describeError } from '../api/http'
import type { Collection, CollectionMeta } from '../lib/collections'
import { validReleaseDate, entryTitle } from '../lib/libraryEntries'
import type { OpenEntry } from '../lib/libraryPlay'
import EntryImage from './EntryImage.vue'
import Modal from './Modal.vue'

export interface CollectionEdit {
  meta: Partial<Pick<CollectionMeta, 'name' | 'artist' | 'release_date' | 'description' | 'cover'>>
  /** Clear the cover (photo or uploaded): back to automatic. */
  autoCover?: boolean
  upload?: File
}

const props = defineProps<{ collection: Collection<OpenEntry>; photos: OpenEntry[]; action: (e: CollectionEdit) => Promise<unknown> }>()
const emit = defineEmits<{ close: [] }>()

const r = props.collection.record?.meta
const form = reactive({
  name: props.collection.name,
  artist: props.collection.artist ?? '',
  release_date: props.collection.release_date ?? '',
  description: props.collection.description ?? '',
})
/** The cover chosen here: a photo's id, an upload, automatic, or unchanged. */
const cover = ref<{ photo: string } | { upload: File } | { auto: true } | null>(null)
const chosenPhoto = computed(() => (cover.value && 'photo' in cover.value ? cover.value.photo : !cover.value ? r?.cover : undefined))
// Photos in it first, then the rest of the library.
const choices = computed(() => {
  const mine = props.collection.items.filter((f) => f.meta.kind === 'image')
  return [...mine, ...props.photos.filter((p) => !mine.includes(p))].slice(0, 60)
})
const busy = ref(false)
const error = ref<string | null>(null)

function onUpload(e: Event): void {
  const f = (e.target as HTMLInputElement).files?.[0]
  if (f) cover.value = { upload: f }
}

async function save(): Promise<void> {
  error.value = null
  if (!form.name.trim()) return void (error.value = 'Give it a name.')
  if (form.release_date.trim() && !validReleaseDate(form.release_date.trim())) {
    return void (error.value = 'Dates look like 1977, 1977-05 or 1977-05-25.')
  }
  const edit: CollectionEdit = {
    meta: {
      name: form.name.trim().slice(0, 200),
      ...(props.collection.type === 'audio' ? { artist: form.artist.trim().slice(0, 300) } : {}),
      ...(props.collection.type !== 'files' ? { release_date: form.release_date.trim() } : {}),
      ...(props.collection.type !== 'audio' ? { description: form.description.trim().slice(0, 2000) } : {}),
    },
  }
  const c = cover.value
  if (c && 'photo' in c) edit.meta.cover = c.photo
  else if (c && 'upload' in c) edit.upload = c.upload
  else if (c) edit.autoCover = true
  busy.value = true
  try {
    await props.action(edit)
    emit('close')
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal :title="collection.type === 'files' ? 'Edit collection' : 'Edit album'" @close="emit('close')">
    <form class="space-y-3" data-testid="collection-edit" @submit.prevent="save">
      <div>
        <label class="label" for="collection-edit-name">Name</label>
        <input id="collection-edit-name" v-model="form.name" class="input" maxlength="200" data-testid="collection-edit-name" />
      </div>
      <div v-if="collection.type !== 'files'" class="grid gap-3 sm:grid-cols-2">
        <div v-if="collection.type === 'audio'">
          <label class="label" for="collection-edit-artist">Artist</label>
          <input id="collection-edit-artist" v-model="form.artist" class="input" maxlength="300" data-testid="collection-edit-artist" />
        </div>
        <div>
          <label class="label" for="collection-edit-date">{{ collection.type === 'audio' ? 'Released' : 'Date' }}</label>
          <input id="collection-edit-date" v-model="form.release_date" class="input" placeholder="2026-07" maxlength="10" />
        </div>
      </div>
      <div v-if="collection.type !== 'audio'">
        <label class="label" for="collection-edit-description">Description</label>
        <textarea id="collection-edit-description" v-model="form.description" class="input min-h-20" maxlength="2000" />
      </div>
      <div>
        <div class="mb-1 flex items-center gap-2">
          <span class="label mb-0 flex-1">Cover</span>
          <label class="btn-outline h-8 cursor-pointer max-md:h-10">
            Upload…
            <input type="file" accept="image/*" class="hidden" data-testid="collection-cover-upload" @change="onUpload" />
          </label>
          <button type="button" class="btn-ghost h-8 max-md:h-10" data-testid="collection-cover-auto" @click="cover = { auto: true }">Automatic</button>
        </div>
        <p class="mono mb-2 text-text-muted" data-testid="collection-cover-choice">
          <template v-if="cover && 'upload' in cover">Upload: {{ cover.upload.name }}</template>
          <template v-else-if="cover && 'auto' in cover">Automatic: folder image, embedded art or the first photo</template>
          <template v-else-if="chosenPhoto">A photo from the library</template>
          <template v-else-if="r?.blobs.art">An uploaded image</template>
          <template v-else>Automatic</template>
        </p>
        <div v-if="choices.length" class="grid grid-cols-5 gap-0.5 sm:grid-cols-6">
          <button
            v-for="p in choices"
            :key="p.entry.id"
            type="button"
            class="relative aspect-square overflow-hidden"
            :class="chosenPhoto === p.entry.id ? 'outline-2 -outline-offset-2 outline-ink' : 'opacity-80 hover:opacity-100'"
            :aria-label="`Use ${entryTitle(p.meta)} as the cover`"
            :data-testid="`cover-photo-${entryTitle(p.meta)}`"
            @click="cover = { photo: p.entry.id }"
          >
            <EntryImage :entry="p" use="thumb"><span class="field-grid block h-full w-full" /></EntryImage>
          </button>
        </div>
      </div>
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="emit('close')">Cancel</button>
        <button class="btn-primary" :disabled="busy" data-testid="collection-edit-save">{{ busy ? 'Saving…' : 'Save' }}</button>
      </div>
    </form>
  </Modal>
</template>
